import { useCallback, useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { validateWordSubmission } from "@/utils/wordValidation";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { clearWordError, isSubmissionEligible } from "@/utils/wordSubmission/validationUtils";
import { generateGameStateSummary } from "@/utils/wordSubmission/stateManagement";
import { processWordSubmission, retryFetchingBotWord } from "@/utils/wordSubmission/submissionHandler";
import { supabase } from "@/integrations/supabase/client";

export const useWordSubmission = (
  gameId: string | null,
  rounds: any[],
  currentWord: string,
  usedWords: Set<string>,
  setRounds: (rounds: any) => void,
  setUsedWords: (usedWords: any) => void,
  setCurrentWord: (word: string) => void,
  setIsRevealed: (isRevealed: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  isSubmitting: boolean,
  setIsSubmitting: (isSubmitting: boolean) => void,
  gameOver: boolean,
  botHasChosenWord: boolean,
  userHasSubmitted: boolean,
  setUserHasSubmitted: (hasSubmitted: boolean) => void,
  wordError: string,
  setWordError: (error: string) => void,
  processingComplete: boolean,
  setProcessingComplete: (complete: boolean) => void,
  botIsThinking: boolean,
  handleProcessBothWords: (aiWord: string, aiExplanation: string | undefined) => Promise<void>
) => {
  const { toast } = useToast();
  const [isProcessingError, setIsProcessingError] = useState(false);
  // Flag to track if a user submission is waiting for bot response
  const [waitingForBotWord, setWaitingForBotWord] = useState(false);

  // Effect to detect when bot has chosen its word after user has submitted
  useEffect(() => {
    // CRITICAL FIX: This useEffect will fire whenever botHasChosenWord changes to true 
    // while userHasSubmitted is true
    if (userHasSubmitted && botHasChosenWord && !isSubmitting && waitingForBotWord) {
      debugLog(DebugLevel.INFO, "wordSubmission", "Bot has chosen word after user submitted. Processing round now.");
      
      // Reset the waiting flag
      setWaitingForBotWord(false);
      
      // Immediately fetch the bot's word from the database
      const fetchBotWordAndProcess = async () => {
        try {
          const roundNumber = rounds.length + 1;
          const { data, error } = await supabase
            .from("rounds")
            .select("ai_word, ai_explanation")
            .eq("game_id", gameId!)
            .eq("round_number", roundNumber)
            .maybeSingle();
            
          if (error) throw error;
          
          if (data && data.ai_word) {
            debugLog(DebugLevel.INFO, "wordSubmission", `Retrieved bot's word: ${data.ai_word}`);
            // Process both words now that we have them
            await handleProcessBothWords(data.ai_word, data.ai_explanation);
          }
        } catch (error) {
          debugLog(DebugLevel.ERROR, "wordSubmission", "Error retrieving bot word:", error);
        }
      };
      
      fetchBotWordAndProcess();
    }
  }, [botHasChosenWord, userHasSubmitted, isSubmitting, waitingForBotWord, rounds.length, gameId, handleProcessBothWords]);

  const handleSubmitWord = useCallback(async () => {
    debugLog(DebugLevel.INFO, "wordSubmission", "handleSubmitWord called with currentWord:", currentWord);
    debugLog(DebugLevel.INFO, "wordSubmission", `isSubmitting: ${isSubmitting}, gameOver: ${gameOver}, botHasChosenWord: ${botHasChosenWord}, userHasSubmitted: ${userHasSubmitted}`);
    
    // Check if submission is eligible
    if (!isSubmissionEligible(userHasSubmitted, isProcessingError, gameOver)) {
      return;
    }
    
    // Generate debug summary of current state
    if (gameId) {
      await generateGameStateSummary(
        gameId,
        rounds.length + 1,
        botIsThinking,
        botHasChosenWord,
        userHasSubmitted,
        processingComplete
      );
    }
    
    // Validate word submission
    const { isValid, normalizedWord, errorMessage } = validateWordSubmission(
      currentWord,
      usedWords,
      gameId,
      false, 
      gameOver
    );
    
    if (!isValid) {
      if (errorMessage) {
        // Set error processing flag to prevent multiple errors
        setIsProcessingError(true);
        
        // Set the word error for display
        setWordError(errorMessage);
        debugLog(DebugLevel.WARN, "wordSubmission", "Word error set:", errorMessage);
        
        // Reset the error processing flag after the error should be cleared
        setTimeout(() => {
          setIsProcessingError(false);
        }, 1800); // Slightly longer than error display time to prevent race conditions
        
        return;
      }
      return;
    }
    
    try {
      // Set submitting state to prevent multiple submissions
      setIsSubmitting(true);
      
      // Get the current round number
      const roundNumber = rounds.length + 1;
      debugLog(DebugLevel.INFO, "wordSubmission", `Submitting word for round ${roundNumber}: ${currentWord.trim()}`);
      
      // Process the word submission
      await processWordSubmission(
        gameId!,
        roundNumber,
        currentWord.trim(),
        usedWords,
        setRounds,
        setUsedWords,
        setCurrentWord,
        setIsRevealed,
        setBotHasChosenWord,
        setBotIsThinking,
        setShouldAutoFocus,
        setGameOver,
        setConvergenceRound,
        setGameStatus,
        setUserHasSubmitted,
        setProcessingComplete,
        setIsSubmitting,
        setWaitingForBotWord,
        toast,
        handleProcessBothWords
      );
    } catch (error) {
      debugLog(DebugLevel.ERROR, "wordSubmission", "Error submitting word:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem submitting your word."
      });
      // Reset user submission state on error
      setUserHasSubmitted(false);
      setProcessingComplete(true);
      setIsSubmitting(false);
    }
  }, [
    currentWord, 
    usedWords,
    rounds.length,
    gameId,
    toast,
    isSubmitting,
    gameOver,
    botHasChosenWord,
    userHasSubmitted,
    setRounds,
    setUsedWords,
    setCurrentWord,
    setIsRevealed,
    setBotHasChosenWord,
    setBotIsThinking,
    setShouldAutoFocus,
    setGameOver,
    setConvergenceRound,
    setGameStatus,
    setIsSubmitting,
    setUserHasSubmitted,
    setWordError,
    isProcessingError,
    processingComplete,
    setProcessingComplete,
    botIsThinking,
    handleProcessBothWords
  ]);

  const handleClearWordError = useCallback(() => {
    clearWordError(wordError, setWordError);
  }, [wordError, setWordError]);

  return { 
    handleSubmitWord,
    handleClearWordError
  };
};
